import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import {
  User,
  CalendarRange,
  Phone,
  MapPin,
  Mail,
  Building,
  ArrowRight,
} from "lucide-react";
import { Button } from "@shared/ui/button";
import { useNavigate } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { useFullName } from "@shared/hooks/useFullName";

// Define the consumer data interface
export interface ConsumerHistoryItem {
  id: string;
  name: string;
  accountNumber: string;
  status: string;
  startDate: string;
  endDate?: string;
  contactPhone?: string;
  contactEmail?: string;
  address?: string;
  category?: string;
}
interface ConsumerHistoryCardProps {
  consumer: Record<string, any>;
}
const ConsumerHistoryCard: React.FC<ConsumerHistoryCardProps> = ({
  consumer,
}) => {
  console.log("Consumer History Card Data: ", consumer);
  const navigate = useNavigate();
  const handleViewConsumer = () => {
    navigate(`/cx/consumer-management/${consumer?.consumerInfo?.consumerNo}`);
  };
  const consumerInfo = consumer?.consumerInfo;
  const consumerName = useFullName(consumerInfo);

  return (
    <Card
      className={`shadow-sm hover:shadow transition-shadow ${
        consumer.status === "CURRENT" ? "border-l-4 border-l-primary" : ""
      }`}
    >
      <CardHeader className="p-3 pb-1">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              Account: {consumerInfo?.consumerNo}
            </CardTitle>
            <div className="text-xs text-muted-foreground">
              {consumerName}
            </div>
          </div>
          <Badge
            className={
              consumer.status.toLowerCase() === "current"
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-800"
            }
          >
            {consumer.status ?? "NA"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-3 pt-2">
        <div className="space-y-1 text-sm">
          <div className="flex flex-col mb-2">
            <div className="flex items-center gap-2 mb-1">
              <CalendarRange className="h-4 w-4 text-muted-foreground" />
              <span>
                {consumer?.assignDate}{" "}
                {consumer?.deassignDate ? (
                  <>
                    {" "}
                    <ArrowRight className="inline h-3 w-3 mx-1" />{" "}
                    {consumer?.deassignDate}
                  </>
                ) : (
                  "- Present"
                )}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-1">
              <div className="flex items-center gap-1">
                <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs truncate">
                  {consumerInfo?.contactNumber ?? "NA"}
                </span>
              </div>

              <div className="flex items-center gap-1">
                <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs truncate">
                  {consumerInfo?.email ?? "NA"}
                </span>
              </div>
              <div className="col-span-2 flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs truncate">
                  {consumerInfo?.territoryData?.service?.address || "NA"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs"
                  onClick={handleViewConsumer}
                >
                  View Details
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Go to consumer profile page</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  );
};
export default ConsumerHistoryCard;
